if (!customElements.get('ss-collection-grid')) {
  customElements.define('ss-collection-grid', class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.results = this.querySelector('[data-ss-results]');
      this.panel = this.querySelector('[data-ss-panel]');
      this.scrim = this.querySelector('[data-ss-scrim]');
      this.form = this.querySelector('[data-ss-filter-form]');
      this.toggle = this.querySelector('[data-ss-open-filters]');

      this.autoApply = this.dataset.autoApply === 'true';
      this.cleanup = [];

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick);
      this.form?.addEventListener('change', this.onChange);
      window.addEventListener('popstate', this.onPopState);

      this.initGroups();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.form?.removeEventListener('change', this.onChange);
      window.removeEventListener('popstate', this.onPopState);
      this.cleanup.forEach((fn) => fn());
      if (this.isOpen) SS.unlockScroll();
    }

    /* ---------- Accordéons du panneau ---------- */

    initGroups() {
      this.querySelectorAll('[data-ss-group-btn]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleGroup(btn));
      });
    }

    toggleGroup(btn) {
      const group = btn.closest('[data-ss-group]');
      const panel = group?.querySelector('.ss-cg__group-panel');
      if (!panel) return;

      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      group.classList.toggle('is-open', open);

      if (SS.reduceMotion()) {
        panel.hidden = !open;
        return;
      }

      if (open) {
        panel.hidden = false;
        const h = panel.scrollHeight;
        panel.style.height = '0px';
        requestAnimationFrame(() => { panel.style.height = `${h}px`; });
        panel.addEventListener('transitionend', function done() {
          panel.style.height = '';
          panel.removeEventListener('transitionend', done);
        });
      } else {
        panel.style.height = `${panel.scrollHeight}px`;
        requestAnimationFrame(() => { panel.style.height = '0px'; });
        panel.addEventListener('transitionend', function done() {
          panel.hidden = true;
          panel.style.height = '';
          panel.removeEventListener('transitionend', done);
        });
      }
    }

    /* ---------- Ouverture / fermeture ---------- */

    onClick(e) {
      if (e.target.closest('[data-ss-open-filters]')) { this.open(); return; }
      if (e.target.closest('[data-ss-close-filters]')) { this.close(); return; }
      if (e.target.closest('[data-ss-scrim]')) { this.close(); return; }

      if (e.target.closest('[data-ss-apply]')) {
        e.preventDefault();
        this.apply();
        return;
      }

      const clear = e.target.closest('[data-ss-clear]');
      if (clear) {
        e.preventDefault();
        this.load(clear.href);
        this.close();
        return;
      }

      const page = e.target.closest('[data-ss-page]');
      if (page) {
        e.preventDefault();
        this.load(page.href, { scroll: true });
      }
    }

    open() {
      if (!this.panel || this.isOpen) return;
      this.isOpen = true;

      this.panel.hidden = false;
      if (this.scrim) this.scrim.hidden = false;

      requestAnimationFrame(() => {
        this.panel.classList.add('is-open');
        this.scrim?.classList.add('is-open');
      });

      this.toggle?.setAttribute('aria-expanded', 'true');

      if (SS.isMobile()) {
        SS.lockScroll();
        this.cleanup.push(SS.trapFocus(this.panel));
      } else {
        this.cleanup.push(SS.onClickOutside(this.panel, (e) => {
          if (!e.target.closest('[data-ss-open-filters]')) this.close();
        }));
      }
      this.cleanup.push(SS.onEscape(() => this.close()));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.panel.classList.remove('is-open');
      this.scrim?.classList.remove('is-open');
      this.toggle?.setAttribute('aria-expanded', 'false');

      this.cleanup.forEach((fn) => fn());
      this.cleanup = [];
      SS.unlockScroll();

      const done = () => {
        if (this.isOpen) return;
        this.panel.hidden = true;
        if (this.scrim) this.scrim.hidden = true;
      };

      if (SS.reduceMotion()) done();
      else setTimeout(done, 320);

      this.toggle?.focus({ preventScroll: true });
    }

    /* ---------- Filtres ---------- */

    onChange(e) {
      if (!this.autoApply) return;
      if (e.target.type === 'number') return; // le prix attend le bouton
      this.apply({ keepOpen: true });
    }

    buildUrl() {
      const params = new URLSearchParams(new FormData(this.form));

      // Nettoie les champs vides
      Array.from(params.entries()).forEach(([key, value]) => {
        if (value === '') params.delete(key);
      });

      const base = window.location.pathname;
      const query = params.toString();
      return query ? `${base}?${query}` : base;
    }

    apply(options = {}) {
      this.load(this.buildUrl());
      if (!options.keepOpen && SS.isMobile()) this.close();
    }

    /* ---------- Chargement ---------- */

    async load(url, options = {}) {
      if (!this.results) return;

      this.results.setAttribute('aria-busy', 'true');
      this.abort?.abort();
      this.abort = new AbortController();

      try {
        const doc = await SS.fetchSection(this.sectionId, url);
        const nextResults = doc.querySelector('[data-ss-results]');
        const nextPanel = doc.querySelector('[data-ss-panel]');
        const nextTools = doc.querySelector('.ss-cg__head-tools');

        if (nextResults) this.results.innerHTML = nextResults.innerHTML;

        // Le panneau se met à jour : les compteurs et valeurs disponibles changent
        if (nextPanel && this.panel) {
          const wasOpen = this.isOpen;
          const scrollTop = this.querySelector('.ss-cg__panel-scroll')?.scrollTop || 0;

          this.panel.innerHTML = nextPanel.innerHTML;
          this.form = this.querySelector('[data-ss-filter-form]');
          this.form?.addEventListener('change', this.onChange);
          this.initGroups();

          const scroller = this.querySelector('.ss-cg__panel-scroll');
          if (scroller) scroller.scrollTop = scrollTop;
          if (wasOpen) this.panel.classList.add('is-open');
        }

        if (nextTools) {
          const tools = this.querySelector('.ss-cg__head-tools');
          if (tools) {
            tools.innerHTML = nextTools.innerHTML;
            this.toggle = this.querySelector('[data-ss-open-filters]');
            this.toggle?.setAttribute('aria-expanded', String(this.isOpen));
          }
        }

        if (!options.replace) {
          window.history.pushState({ ssUrl: url }, '', url);
        }

        window.ssScanAnimations?.(this.results);

        if (options.scroll) SS.scrollTo(this, 100);
      } catch (err) {
        console.error('SS — filtres :', err);
        window.location.href = url;
      } finally {
        this.results.setAttribute('aria-busy', 'false');
      }
    }

    onPopState() {
      this.load(window.location.href, { replace: true });
    }
  });
}